import { useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { ListRootDrive } from '@/modules/Drive/services'
import useDriveStore from '@/stores/useDriveStore'
import FileGrid from '../FileGrid'
import { useTranslation } from 'react-i18next'
import ViewTypeSwitch from '../ViewTypeSwitch'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useSearchParams } from 'react-router-dom'
import usePermissionsStore from '@/stores/usePermissionsStore'
import StarredFile from './StarredFile'
import MyDrive from './MyDrive'
import RecentFile from './RecentFile'

const RootDrive = () => {
    const { isAdmin } = usePermissionsStore()
    const { t } = useTranslation()
    const viewType = useDriveStore((state) => state.viewType)
    const setViewType = useDriveStore((state) => state.setViewType)

    const [searchParams] = useSearchParams()
    const searchQuery = searchParams.get('search')?.toLowerCase() ?? ''

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ROOT_DRIVE],
        queryFn: () => ListRootDrive(),
        enabled: isAdmin,
    })

    const items = useMemo(
        () => ({
            folders:
                data?.result.folders.filter(
                    (folder) =>
                        folder.Prefix.replace(/\/$/, '')
                            .split('/')
                            .pop()
                            ?.toLowerCase()
                            ?.includes(searchQuery) ||
                        folder.FolderName?.toLowerCase()?.includes(searchQuery)
                ) || [],
            files:
                data?.result.files.filter((file) =>
                    file.Key.split('/')
                        .pop()
                        ?.toLowerCase()
                        ?.includes(searchQuery)
                ) || [],
        }),
        [data, searchQuery]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!isAdmin) {
        return (
            <div className="flex flex-col gap-6">
                <MyDrive />
                <StarredFile />
                <RecentFile />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <header className="flex items-center justify-between">
                <div className="font-semibold text-primary text-base flex items-center gap-3">
                    <span>{t('root-drive-title')}</span>
                </div>
                <ViewTypeSwitch value={viewType} onChange={setViewType} />
            </header>

            {items.folders.length === 0 && items.files.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">{t('no-results')}</div>
                </div>
            ) : (
                <FileGrid folders={items.folders} files={items.files} />
            )}
        </div>
    )
}

export default RootDrive
